/**
 * Overlays: the focus-trap and Escape hooks, the modal dialog and the side
 * panel that slides in from the inline end. Re-exported through
 * `./primitives.js`.
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';

import { IconButton } from './controls.js';
import { CloseIcon } from './icons.js';
import { Eyebrow } from './surfaces.js';

/* ---- Overlays -------------------------------------------------------- */

/** Tab stays inside the element; the first control is focused on open. */
export function useFocusTrap(open: boolean): {
  readonly ref: (node: HTMLElement | null) => void;
  readonly onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
} {
  const nodeRef = useRef<HTMLElement | null>(null);
  const restore = useRef<Element | null>(null);
  const ref = useCallback((node: HTMLElement | null) => {
    nodeRef.current = node;
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    restore.current = document.activeElement;
    const timer = window.setTimeout(() => {
      const root = nodeRef.current;
      if (root === null) return;
      const target =
        root.querySelector<HTMLElement>('[data-autofocus]') ??
        root.querySelector<HTMLElement>(
          'input,textarea,select,button:not([disabled]),a[href],[tabindex="0"]',
        );
      target?.focus();
    }, 30);
    return () => {
      window.clearTimeout(timer);
      const previous = restore.current;
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [open]);
  const onKeyDown = useCallback((event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Tab') return;
    const root = event.currentTarget;
    const focusable = Array.from(
      root.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input,select,textarea,[tabindex="0"]',
      ),
    ).filter((element) => element.offsetParent !== null);
    const first = focusable[0];
    const last = focusable.at(-1);
    if (first === undefined || last === undefined) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);
  return { ref, onKeyDown };
}

export function useEscape(active: boolean, onEscape: () => void): void {
  useEffect(() => {
    if (!active) return undefined;
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onEscape();
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [active, onEscape]);
}

export function Modal({
  title,
  description,
  onClose,
  onSubmit,
  footer,
  children,
}: {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly onClose: () => void;
  readonly onSubmit?: (() => void) | undefined;
  readonly footer: ReactNode;
  readonly children: ReactNode;
}): ReactNode {
  const titleId = useId();
  const trap = useFocusTrap(true);
  useEscape(true, onClose);
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
  return (
    <div
      className="mj-ws-mscrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <form
        ref={trap.ref}
        className="mj-ws-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        noValidate
        onKeyDown={trap.onKeyDown}
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit?.();
        }}
      >
        <h2 id={titleId} className="mj-ws-h2">
          {title}
        </h2>
        {description !== undefined ? (
          <p className="mj-ws-muted mj-ws-sm mj-ws-modal-desc">{description}</p>
        ) : null}
        {children}
        <div className="mj-ws-mfoot">{footer}</div>
      </form>
    </div>
  );
}

/** The wireframe's side panel (video details), from the inline end. */
export function SidePanel({
  title,
  eyebrow,
  onClose,
  closeLabel,
  children,
  footer,
}: {
  readonly title: ReactNode;
  readonly eyebrow: ReactNode;
  readonly onClose: () => void;
  readonly closeLabel: string;
  readonly children: ReactNode;
  readonly footer: ReactNode;
}): ReactNode {
  const titleId = useId();
  const trap = useFocusTrap(true);
  useEscape(true, onClose);
  return (
    <>
      <div className="mj-ws-mscrim is-panel" onMouseDown={onClose} />
      <aside
        ref={trap.ref}
        className="mj-ws-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={trap.onKeyDown}
      >
        <div className="mj-ws-panel-head">
          <div className="mj-ws-panel-title">
            <Eyebrow>{eyebrow}</Eyebrow>
            <h2 id={titleId} className="mj-ws-h2">
              {title}
            </h2>
          </div>
          <IconButton label={closeLabel} onClick={onClose} autoFocus>
            <CloseIcon />
          </IconButton>
        </div>
        <div className="mj-ws-panel-body">{children}</div>
        <div className="mj-ws-panel-foot">{footer}</div>
      </aside>
    </>
  );
}
